import { ASSIGNED_SEX, SKIN_COLOR } from '@/constants/person'
import { routes as api } from '@/plugins/api'
import { util } from '@/plugins/util'
import state from '../auth/state'

/*
Store criada para centralizar as informações do paciente, facilitando a manipulação e a exibição desses dados.

Antes, as informações estavam espalhadas por diversos componentes e serviços através de props e eventos.
Isso gerava um acoplamento desnecessário e dificultava a manutenção do código.

! Obs: Dividir esse arquivo em cinco fragmentos vai dificultar a leitura e entendimento do código,
! sem nenhum benefício aparente. Apenas um arquivo com todas as partes é suficiente.
! Se for necessário, use o collapse da sua IDE para esconder partes do código.
*/

function parseInts(patient) {
  if (typeof patient.currentWeightKg === 'string') {
    patient.currentWeightKg = parseInt(patient.currentWeightKg)
  }
  if (typeof patient.currentHeightCm === 'string') {
    patient.currentHeightCm = parseInt(patient.currentHeightCm)
  }
}

export default {
  namespaced: true,
  state: {
    patient: null,
    isLoading: false,
  },
  getters: {
    patient: (state) => state.patient,
    patientStoreIsLoading: (state) => state.isLoading,
    patientId: (state) => state.patient?.id,
    patientActive: (state) => state.patient?.active,
    patientCreatedAt: (state) => state.patient?.createdAt,
    patientUpdatedAt: (state) => state.patient?.updatedAt,
    patientName: (state) => state.patient?.name,
    patientSocialName: (state) => state.patient?.socialName,
    patientBirthday: (state) => state.patient?.birthday,
    patientCity: (state) => state.patient?.city,
    patientAddress: (state) => state.patient?.address,
    patientNumber: (state) => state.patient?.number,
    patientComplement: (state) => state.patient?.complement,
    patientDistrict: (state) => state.patient?.district,
    patientZipCode: (state) => state.patient?.zipCode,
    patientMotherName: (state) => state.patient?.motherName,
    patientCurrentHeightCm: (state) => state.patient?.currentHeightCm,
    patientCurrentWeightKg: (state) => state.patient?.currentWeightKg,
    patientAssignedSex: (state) => state.patient?.assignedSex,
    patientSkinColor: (state) => state.patient?.skinColor,
    patientHasHealthPlan: (state) => state.patient?.hasHealthPlan,
    patientHealthPlan: (state) => state.patient?.healthPlan,
    patientHealthPlanInitialDate: (state) => state.patient?.healthPlanInitialDate,
    patientAcceptedTerms: (state) => state.patient?.acceptedTerms,
    patientTestPerson: (state) => state.patient?.testPerson,
    patientFromChatbot: (state) => state.patient?.fromChatbot,
    patientOrigin: (state) => state.patient?.origin,
    patientAge: (state) => state.patient?.age,
    patientUuid: (state) => state.patient?.uuid,
    patientCpf: (state) => state.patient?.cpf,
    patientEmail: (state) => state.patient?.email,
    patientPhone: (state) => state.patient?.phone,
    patientMobile: (state) => state.patient?.mobile || state.patient?.number,
    patientAppointments: (state) => state.patient?.appointments,
    patientCpfText: (state) => util.formatCpf(state.patient?.cpf),
    patientMaskedCpfText: (state) => util.maskCpf(state.patient?.cpf),
    patientBirthdayText: (state) => {
      if (!state.patient?.birthday) return null
      const dateBirthday = new Date(state.patient.birthday)
      const formattedBirthday = util.toFormattedDate(state.patient.birthday)
      const today = new Date()
      const age = today.getFullYear() - dateBirthday.getFullYear()
      const subtractOneYear = today < new Date(today.getFullYear(), dateBirthday.getMonth(), dateBirthday.getDate())

      return `${formattedBirthday} (${age - (subtractOneYear ? 1 : 0)} anos)`
    },
    patientAssignedSexText: (state) => {
      if (!state.patient?.assignedSex) return null
      const patientAssignedSex = state.patient.assignedSexs
      return ASSIGNED_SEX.find((assignedSex) => patientAssignedSex === assignedSex.value)?.text
    },
    patientSkinColorText: (state) => {
      if (!state.patient?.skinColor) return null
      const patientSkinColor = state.patient.skinColor
      return SKIN_COLOR.find((skinColor) => patientSkinColor === skinColor.value)?.text
    },
    patientCurrentHeightText: (state) => {
      if (!state.patient?.currentHeightCm) return null
      return `${state.patient.currentHeightCm} cm`
    },
    patientCurrentWeightText: (state) => {
      if (!state.patient?.currentWeightKg) return null
      return `${state.patient.currentWeightKg} kg`
    },
  },
  mutations: {
    setPatient(state, patient) {
      parseInts(patient)
      state.patient = patient
    },
    setPatientCurrentWeightKg(state, currentWeightKg) {
      state.patient.currentWeightKg = currentWeightKg
      parseInts(state.patient)
    },
    setPatientCurrentHeightCm(state, currentHeightCm) {
      state.patient.currentHeightCm = currentHeightCm
      parseInts(state.patient)
    },
    setPatientHealthPlan(state, patientHealthPlan) {
      state.patient.healthPlan = patientHealthPlan
    },
    setPatientCity(state, patientCity) {
      state.patient.city = patientCity
    },
    setPatientMobile(state, patientMobile) {
      state.patient.number = patientMobile
    },
    setPatientEmail(state, patientEmail) {
      state.patient.email = patientEmail
    },
  },
  actions: {
    /*
    Lidar com chamadas de API no Vuex é uma prática recomendada, pois mantém a lógica de chamada de API
    em um único local, facilitando todos os aspectos da manutenção do código.
    */
    async fetchPatientById({ commit }, patientId) {
      state.isLoading = true
      try {
        const patient = await api.getPatientByIdWithAttendances(patientId)
        commit('setPatient', patient)
      } finally {
        state.isLoading = false
      }
    },
    async patchPatient({ state }) {
      if (!state.patient) {
        throw new Error('patient store está vazia. Use setPatient ou fetchPatientById antes.')
      }
      state.isLoading = true
      try {
        await api.updatePatient(state.patient)
      } finally {
        state.isLoading = false
      }
    },
    // ! Essa store só deve editar os campos que o usuário pode editar.
    // ! Nunca use a action abaixo para alterar outros campos.
    setPatient({ commit }, patient) {
      commit('setPatient', patient)
    },
    setPatientCurrentWeightKg({ commit }, newPatientCurrentWeightKg) {
      commit('setPatientCurrentWeightKg', newPatientCurrentWeightKg)
    },
    setPatientCurrentHeightCm({ commit }, newPatientHeight) {
      commit('setPatientCurrentHeightCm', newPatientHeight)
    },
    setPatientHealthPlan({ commit }, newPatientHealthPlan) {
      commit('setPatientHealthPlan', newPatientHealthPlan)
    },
    setPatientCity({ commit }, newPatientCity) {
      commit('setPatientCity', newPatientCity)
    },
    setPatientMobile({ commit }, newPatientMobile) {
      commit('setPatientMobile', newPatientMobile)
    },
    setPatientEmail({ commit }, newPatientEmail) {
      commit('setPatientEmail', newPatientEmail)
    },
  },
}
